import { useTranslation } from 'next-i18next/pages';
import cn from 'clsx';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { decodeProfileValues, isFullListing, type UserAccountExclusiveListing, type Listing } from '@pkgs/api';
import { Headline } from '@ev-tech/ui-core-components/Headline';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useShouldHideUserAccountExclusiveContent } from '@/hooks/useShouldHideUserAccountExclusiveContent';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { getPreferredProfileContent } from '@/components/property-expose/RichText/RichText';
import { ExposeLanguageSelector } from '@/components/property-expose/ExposeLanguageSelector/ExposeLanguageSelector';
import { ExpandableCopy } from '@/components/property-expose/ExpandableCopy/ExpandableCopy';

import styles from './PropertyDescription.module.css';

export const PropertyDescription = ({ className, listing }: { className?: string; listing: Listing | UserAccountExclusiveListing }) => {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  const profile = decodeProfileValues(listing.profile);
  const { trackExposeUserInteractionEvent } = useExposeTracking();
  const descriptionContent = getPreferredProfileContent({
    richText: profile.descriptionRich,
    text: profile.description,
  });

  const shouldHideUserAccountExclusiveContent = useShouldHideUserAccountExclusiveContent();

  if (!descriptionContent) return null;

  return (
    <div className={cn(styles.propertyDescription, className)} data-testid="expose-property-description">
      <Headline
        size="h5"
        as="h2"
        label={t(
          isFullListing(listing) && listing.isDevelopmentProject
            ? 'search.expose.propertyDetails.development.label.headline'
            : 'search.expose.propertyDetails.label.headline',
        )}
        data-testid="expose-property-description-headline"
      />
      {isFullListing(listing) && <ExposeLanguageSelector listing={listing} />}
      {descriptionContent && (
        <ExpandableCopy
          className={cn(styles.expandableCopy, shouldHideUserAccountExclusiveContent && styles.propertyDescriptionContentOverlay)}
          onExpandButtonClick={(isOpen: boolean) =>
            trackExposeUserInteractionEvent({
              elementId: isOpen ? 'property-info_show-less' : 'property-info_show-more',
            })
          }
          content={descriptionContent}
          lines={isMobile ? 7 : 14}
          data-testid="expose-property-description-text"
        />
      )}
    </div>
  );
};
